import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton, CircularProgress, Switch, Tooltip } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import MessageIcon from '@material-ui/icons/Message';
import { i18n } from '../../translate/i18n';
import { toast } from 'react-toastify';
import api from '../../services/api';
import KanbanAutoMessageModal from './KanbanAutoMessageModal';

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: props.color || '#ebecf0',
    borderRadius: 8,
    minWidth: 272,
    maxWidth: 272,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    maxHeight: '100%',
  }),
  columnHeader: {
    padding: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  headerBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
    width: '100%',
    padding: '0 4px',
  },
  headerAutoMsg: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    padding: '4px 4px 0 4px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  columnTitle: {
    fontWeight: 'bold',
    fontSize: '0.9rem',
    color: "#FFFFFF",
    textShadow: '0px 1px 2px rgba(0,0,0,0.5)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
  },
  totalValue: {
    fontSize: '0.75rem',
    color: "#FFFFFF",
    fontWeight: '500',
    textShadow: '0px 1px 2px rgba(0,0,0,0.5)',
  },
  autoMsgLabel: {
    fontSize: '0.75rem',
    color: '#FFFFFF',
    fontWeight: '500',
    textShadow: '0px 1px 2px rgba(0,0,0,0.5)',
  },
  dragHandle: {
    cursor: 'grab',
    color: '#FFFFFF',
    marginRight: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))',
    '&:hover': {
      color: '#EEEEEE',
    },
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(1),
    minHeight: 150,
    transition: 'background-color 0.2s ease',
    borderRadius: 6,
    ...(theme.scrollbarStylesNeutral || theme.scrollbarStyles),
  },
  cardListDraggingOver: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    boxShadow: 'inset 0 0 14px rgba(255, 255, 255, 0.25)',
    outline: '2px dashed rgba(255, 255, 255, 0.5)',
    outlineOffset: '-4px',
  },
  msgIcon: {
    color: '#FFFFFF',
    padding: 2,
    filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))',
    '&:hover': {
      color: '#EEEEEE',
    },
  },
  switchBase: {
    color: '#FAFAFA',
    filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.5))',
    '&$checked': {
      color: '#4caf50',
    },
    '&$checked + $track': {
      backgroundColor: '#4caf50',
    },
  },
  checked: {},
  track: {},
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, isAdmin, laneTags, boardId, boardLanes, handleOpenTagMenu, laneTotals, lanePagination, fetchMoreTickets, sendGreetingMessage, greetingMessageLane, kanbanMessageDelay, kanbanMessageDelayUnit, greetingMessageKanban, greetingMessageInbox, greetingMessageFlowBuilder, greetingMessageCampaigns, currency = "$", onValueChange }) => {
  const classes = useStyles({ color });
  const [autoMessageEnabled, setAutoMessageEnabled] = useState(sendGreetingMessage === true || String(sendGreetingMessage) === 'true' || String(sendGreetingMessage) === '1');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setAutoMessageEnabled(sendGreetingMessage === true || String(sendGreetingMessage) === 'true' || String(sendGreetingMessage) === '1');
  }, [sendGreetingMessage]);

  const totalValue = (laneTotals && laneTotals[id] !== undefined && laneTotals[id].value !== undefined)
    ? laneTotals[id].value
    : tickets.reduce((acc, ticket) => {
        const customFields = ticket.contact.extraInfo || [];
        const valueField = customFields.find(field => field.name === 'valor');
        const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
        return acc + opportunityValue;
      }, 0);

  const numericTotalValue = typeof totalValue === 'number' && !isNaN(totalValue)
    ? totalValue
    : (parseFloat(totalValue) || 0);

  const totalCount = (laneTotals && laneTotals[id] !== undefined && laneTotals[id].count !== undefined)
    ? laneTotals[id].count
    : tickets.length;

  const numericTotalCount = typeof totalCount === 'number' && !isNaN(totalCount)
    ? totalCount
    : (parseInt(totalCount, 10) || 0);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (lanePagination && lanePagination[id] && lanePagination[id].hasMore) {
        fetchMoreTickets(id);
      }
    }
  };

  const handleToggleAutoMessage = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setAutoMessageEnabled(true);
      setModalOpen(true);
    } else {
      setAutoMessageEnabled(false);
      api.get('/tags/' + id).then(function(res) {
        return api.put('/tags/' + id, Object.assign({}, res.data, { sendGreetingMessage: false }));
      }).then(function() {
        toast.success(i18n.t("kanban.autoMessage.disabledSuccess", "Mensaje automático desactivado"));
      }).catch(function() {
        toast.error("Error al desactivar el mensaje automático");
        setAutoMessageEnabled(true);
      });
    }
  };

  const handleSaveMessage = function(message, delay, kanban, inbox, flowBuilder, campaigns, delayUnit) {
    api.get('/tags/' + id).then(function(res) {
      return api.put('/tags/' + id, Object.assign({}, res.data, { 
        sendGreetingMessage: true, 
        greetingMessageLane: message, 
        kanbanMessageDelay: delay,
        kanbanMessageDelayUnit: delayUnit,
        greetingMessageKanban: kanban,
        greetingMessageInbox: inbox,
        greetingMessageFlowBuilder: flowBuilder,
        greetingMessageCampaigns: campaigns
      }));
    }).then(function() {
      toast.success(i18n.t("kanban.autoMessage.savedSuccess"));
      setAutoMessageEnabled(true);
      setModalOpen(false);
    }).catch(function() {
      toast.error("Error al guardar el mensaje");
      setAutoMessageEnabled(false);
      setModalOpen(false);
    });
  };

  return (
    <Draggable draggableId={`column-${id}`} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.columnHeader}>
            <div className={classes.headerTop}>
              {isAdmin && (
                <div
                  {...provided.dragHandleProps}
                  className={classes.dragHandle}
                >
                  <DragIndicatorIcon fontSize="small" />
                </div>
              )}
              <Typography className={classes.columnTitle}>{title}</Typography>
            </div>
            <div className={classes.headerBottom}>
              <Typography className={classes.totalValue}>
                {i18n.t('kanban.columns.total', { count: numericTotalCount })}
              </Typography>
              <Typography className={classes.totalValue}>
                {currency ? `${currency} ${numericTotalValue.toFixed(2)}` : i18n.t('kanban.columns.currency', { value: numericTotalValue.toFixed(2) })}
              </Typography>
            </div>
            {isAdmin && index !== 0 && title?.toLowerCase() !== 'inicio' && (
              <div className={classes.headerAutoMsg}>
                <Typography className={classes.autoMsgLabel}>
                  {i18n.t("kanban.autoMessage.switchLabel", "Bienvenida auto")}
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {autoMessageEnabled && (
                    <Tooltip title={i18n.t("kanban.autoMessage.editTooltip", "Editar mensaje")}>
                      <IconButton
                        size="small"
                        className={classes.msgIcon}
                        onClick={() => setModalOpen(true)}
                      >
                        <MessageIcon style={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Switch
                    size="small"
                    checked={autoMessageEnabled}
                    onChange={handleToggleAutoMessage}
                    classes={{
                      switchBase: classes.switchBase,
                      checked: classes.checked,
                      track: classes.track,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <KanbanAutoMessageModal
            open={modalOpen}
            onClose={() => { setModalOpen(false); if (!greetingMessageLane) setAutoMessageEnabled(false); }}
            onSave={handleSaveMessage}
            initialMessage={greetingMessageLane}
            initialDelay={kanbanMessageDelay}
            initialDelayUnit={kanbanMessageDelayUnit}
            initialKanban={greetingMessageKanban}
            initialInbox={greetingMessageInbox}
            initialFlowBuilder={greetingMessageFlowBuilder}
            initialCampaigns={greetingMessageCampaigns}
          />
          <Droppable droppableId={id.toString()} type="CARD">
            {(provided, snapshot) => (
              <div
                className={`${classes.cardList} ${snapshot.isDraggingOver ? classes.cardListDraggingOver : ''}`}
                ref={provided.innerRef}
                {...provided.droppableProps}
                onScroll={handleScroll}
              >
                {tickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                    laneTags={laneTags}
                    boardId={boardId}
                    columnColor={color}
                    laneTitle={title}
                    boardLanes={boardLanes}
                    laneId={id}
                    handleOpenTagMenu={handleOpenTagMenu}
                    currency={currency}
                    onValueChange={onValueChange}
                  />
                ))}
                {provided.placeholder}
                {lanePagination && lanePagination[id] && lanePagination[id].hasMore && (
                  <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <CircularProgress size={24} />
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default React.memo(KanbanColumn);
